/**
 * Designer-side rules for adding or reparenting objects on template pages.
 *
 * These rules intentionally live outside the canvas and the sidebar.  The
 * sidebar uses them to present the available widgets; the layer uses them to
 * protect every mutating path.  Loading an existing design does not use these
 * guards, so malformed legacy templates remain editable.
 */

export function isTemplatePage(node) {
    return node?.obj === 'template_page' || node?.isType?.('template_page');
}

export function isPage(node) {
    return node?.obj === 'page' || node?.obj === 'template_page' ||
        node?.isType?.('page') || node?.isType?.('template_page');
}

export function templateRoots(templatePage, { excluding = null } = {}) {
    if (!isTemplatePage(templatePage)) return [];
    // Hasp pages contain Konva implementation children (background rect,
    // shadow and designer id text) in addition to the actual design objects.
    // They share the page's `obj` attribute for rendering, but are not
    // HaspObjects and must never count as template roots. Plain object trees
    // are retained for import/test models that do not use Konva instances.
    const isKonvaPage = typeof templatePage.getLayer === 'function';
    return (templatePage.getChildren?.() ?? [])
        .filter(child => child !== excluding &&
            (!isKonvaPage || (child?.haspid !== undefined && typeof child.export === 'function')) &&
            child?.obj !== 'object_menu' &&
            child?.obj !== 'designer-off-page-mask' && !isPage(child));
}

/** Find the template page that owns a node, if any. */
export function getTemplatePage(node) {
    let current = node;
    while (current) {
        if (isTemplatePage(current)) return current;
        current = current.getParent?.() ?? current.parent;
    }
    return null;
}

/**
 * Return whether a concrete parent accepts a widget type.
 *
 * `tabview`, `btnmatrix` and `msgbox` have special Designer addItem
 * implementations.  Their toolbar-facing child types are deliberately used
 * here as well, so the UI and the actual mutation path agree.
 */
export function parentSupportsObject(parent, objectType) {
    if (!parent || isPage(parent) && !isTemplatePage(parent)) {
        // A regular page accepts all page-level widgets except a tab.
        return Boolean(parent) && objectType !== 'tab';
    }
    if (isTemplatePage(parent)) {
        return objectType !== 'tab' && objectType !== 'page' && objectType !== 'template_page';
    }
    if (parent.isType?.('tabview')) return objectType === 'tab';
    if (parent.isType?.('btnmatrix') || parent.isType?.('msgbox')) return objectType === 'btn';
    return Boolean(parent.isContainer?.()) && objectType !== 'tab';
}

/**
 * Validate adding one object to a parent.  `movingObject` is excluded from
 * the root count when a tree drag reparents an existing object.
 */
export function canAddObjectToParent(parent, objectType, { movingObject = null, templatePage = null } = {}) {
    const rootPage = templatePage ?? (isTemplatePage(parent) ? parent : null);

    // A template with no roots has an implicit root insertion target.  The
    // canvas will use the template page as its technical Konva parent, while
    // the object remains a true root because template pages never export as a
    // runtime parent.
    if (!parent) {
        if (isTemplatePage(rootPage) && templateRoots(rootPage).length === 0 &&
            parentSupportsObject(rootPage, objectType)) {
            return { valid: true, parent: rootPage, root: true };
        }
        return { valid: false, reason: 'The selected object cannot contain children.' };
    }
    if (rootPage && getTemplatePage(parent) !== rootPage) {
        return { valid: false, reason: 'The selected parent is not on the active template page.' };
    }
    if (!parentSupportsObject(parent, objectType)) {
        return { valid: false, reason: `${parent.obj} cannot contain ${objectType} objects.` };
    }

    if (isTemplatePage(parent)) {
        const roots = templateRoots(parent, { excluding: movingObject });
        if (roots.length >= 1) {
            return { valid: false, reason: 'A template can contain only one root object.' };
        }
    }
    return { valid: true };
}

/** Validate a batch of root objects being inserted below one parent. */
export function canAddObjectsToParent(parent, objectTypes, options = {}) {
    const types = (objectTypes ?? []).filter(Boolean);
    if (types.length === 0) return { valid: false, reason: 'No objects to add.' };

    if (!parent && isTemplatePage(options.templatePage) && templateRoots(options.templatePage).length === 0) {
        if (types.length > 1) return { valid: false, reason: 'A template can contain only one root object.' };
        return canAddObjectToParent(null, types[0], options);
    }

    if (isTemplatePage(parent)) {
        const roots = templateRoots(parent, { excluding: options.movingObject });
        if (roots.length + types.length > 1) {
            return { valid: false, reason: 'A template can contain only one root object.' };
        }
    }
    for (const type of types) {
        const result = parentSupportsObject(parent, type)
            ? { valid: true }
            : { valid: false, reason: `${parent?.obj ?? 'The selected object'} cannot contain ${type} objects.` };
        if (!result.valid) return result;
    }
    return { valid: true };
}

/**
 * Resolve the parent represented by the current selection on a template.
 * With no root yet, selecting the page is the valid root insertion target.
 */
export function templateSelectionParent(selectedNode, activePage) {
    if (!isTemplatePage(activePage)) return null;
    if (selectedNode && selectedNode !== activePage && getTemplatePage(selectedNode) !== activePage) {
        return null;
    }
    return selectedNode ?? activePage;
}
